import React from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";

import { FIELD_TYPE_SHORT, isDisplayOnlyType } from "./formFieldHelpers";
import PipedLabel from "./FormPipedLabel";

/**
 * Left rail: the form's field list with drag-to-reorder, selection, and a
 * type-picker "+ Add field" at the bottom. Mirrors the quiz editor's
 * QuestionListPane. Presentational — selection/reorder/delete bubble up.
 */
export function FormFieldListPane({
  fields,
  selectedIndex,
  hoveredIndex,
  onHover,
  onSelect,
  onRemove,
  onDragEnd,
  onOpenAddMenu,
}) {
  return (
    <div className="w-[300px] shrink-0 border-r border-slate-200 bg-white flex flex-col overflow-hidden">
      <div className="h-14 px-4 border-b border-slate-200 flex items-center shrink-0">
        <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
          Fields ({fields.length})
        </p>
      </div>

      <div className="flex-1 overflow-y-auto">
        {fields.length === 0 ? (
          <div className="px-4 py-12 text-center">
            <p className="text-xs text-slate-500">No fields yet. Add one below.</p>
          </div>
        ) : (
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="form-fields">
              {(provided) => (
                <div {...provided.droppableProps} ref={provided.innerRef} className="py-1">
                  {fields.map((field, index) => (
                    <Draggable key={field.id} draggableId={String(field.id)} index={index}>
                      {(dragProvided, snapshot) => (
                        <div
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          style={{
                            ...dragProvided.draggableProps.style,
                            left: "auto !important",
                            top: "auto !important",
                          }}
                        >
                          <FieldRailRow
                            field={field}
                            index={index}
                            earlierFields={fields.slice(0, index)}
                            isSelected={index === selectedIndex}
                            isDragging={snapshot.isDragging}
                            isHovered={index === hoveredIndex}
                            onMouseEnter={() => onHover(index)}
                            onMouseLeave={() => onHover(null)}
                            onSelect={() => onSelect(index)}
                            onRemove={() => onRemove(index)}
                            dragHandleProps={dragProvided.dragHandleProps}
                          />
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </div>

      <div className="h-14 border-t border-slate-200 px-3 bg-white flex items-center shrink-0">
        <button
          type="button"
          onClick={(e) => onOpenAddMenu(e.currentTarget)}
          className="w-full py-2 px-3 bg-violet-50 hover:bg-violet-100 text-violet-700 text-sm rounded-lg flex items-center justify-center gap-2 font-medium transition-colors"
        >
          <AddIcon fontSize="small" />
          Add field
        </button>
      </div>
    </div>
  );
}

function FieldRailRow({
  field,
  index,
  earlierFields,
  isSelected,
  isDragging,
  isHovered,
  onMouseEnter,
  onMouseLeave,
  onSelect,
  onRemove,
  dragHandleProps,
}) {
  // Headings and info screens are display-only: they read as dividers/screens
  // in the rail, not numbered questions.
  const isDisplayOnly = isDisplayOnlyType(field.type);
  const isInfoScreen = field.type === "info_screen";
  // Piped tokens read as the question they pull from, exactly as they do on the
  // card in the centre — the rail showed the raw {{field:id}} until now, so the
  // same question read two different ways side by side.
  const label =
    (field.label || "").trim() ||
    (isInfoScreen
      ? "Info screen"
      : isDisplayOnly
      ? "Section heading"
      : `Field ${index + 1}`);

  return (
    <div
      onClick={onSelect}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={[
        "group flex items-start gap-2 pl-1 pr-2 py-2.5 cursor-pointer transition-colors relative border-b border-slate-100",
        isDragging
          ? "bg-violet-50 shadow-md"
          : isSelected
          ? "bg-violet-50"
          : "bg-white hover:bg-slate-50",
      ].join(" ")}
    >
      {isSelected && (
        <div className="h-full w-1 bg-violet-700 absolute left-0 top-0" />
      )}
      <div
        className="text-slate-400 cursor-grab hover:text-slate-600 mt-0.5"
        {...dragHandleProps}
        onClick={(e) => e.stopPropagation()}
      >
        <DragIndicatorIcon fontSize="small" />
      </div>

      {isDisplayOnly ? (
        <div className="w-7 h-7 rounded bg-slate-200 flex items-center justify-center shrink-0 mt-0.5">
          <span className="text-slate-500 text-xs font-bold leading-none">
            {isInfoScreen ? "T" : "§"}
          </span>
        </div>
      ) : (
        <div className="w-7 h-7 rounded bg-violet-600 flex items-center justify-center shrink-0 mt-0.5">
          <span className="text-white text-xs font-semibold">{index + 1}</span>
        </div>
      )}

      <div className="flex-1 min-w-0">
        <p
          className={[
            "text-sm line-clamp-2 leading-snug",
            isDisplayOnly
              ? "text-slate-500 font-semibold uppercase tracking-wide text-xs mt-1"
              : "text-slate-900",
          ].join(" ")}
        >
          <PipedLabel text={label} earlierFields={earlierFields} />
        </p>
        {!isDisplayOnly && (
          <div className="flex items-center gap-1 mt-1 flex-wrap">
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-600">
              {FIELD_TYPE_SHORT[field.type] || field.type}
            </span>
            {field.required && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700">
                Required
              </span>
            )}
            {field.scored && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-100 text-emerald-700">
                Scored
              </span>
            )}
            {field.show_if && (
              <span
                className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-sky-100 text-sky-700"
                title="Only shown when an earlier answer matches a condition"
              >
                Conditional
              </span>
            )}
          </div>
        )}
        {isDisplayOnly && field.show_if && (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-sky-100 text-sky-700 mt-1">
            Conditional
          </span>
        )}
      </div>

      <Tooltip title="Delete field">
        <span
          className={[
            "transition-opacity",
            isHovered || isSelected ? "opacity-100" : "opacity-0",
          ].join(" ")}
        >
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
          >
            <CloseIcon style={{ fontSize: 14 }} />
          </IconButton>
        </span>
      </Tooltip>
    </div>
  );
}
